import subscriptionCRUDActions from '@mm/state/actions/SubscriptionCRUDActions';
import { getPlanInfoFromBillingType } from '@mm/state/helpers/billing-info-helpers';
import {
  SubscriptionTabDefinition,
  TeamMessageStatistics,
  TEAM_TRIAL_SUBSCRIPTION_PLAN_CODE,
} from '@mm/state/helpers/billing-subscription-tab-helpers';
import { PageBillingType } from '@mm/state/interfaces/connected-page';
import {
  AllotmentDefinition,
  EnhancedPlan,
  PlanBillingPeriodBE,
  PlansAllotments,
} from '@mm/state/interfaces/subscription';
import { SubscriptionModel } from '@mm/state/model/SubscriptionModel';
import modalActions, {
  OpenBillingModalParams,
} from '@mm/state/modules/modalsState/actions';
import {
  activeFacebookPageSelector,
  enhancedBillingPagesSelector,
} from '@mm/state/selectors/facebookPageSelector';
import {
  defaultTeamPlanSelector,
  enhancedPlansSelector,
  getCurrentSubscription,
  getPlansUserSeats,
  selectAccountDetails,
} from '@mm/state/selectors/subscriptionSelectors';
import {
  activeCompanySelector,
  companyAdminSelector,
} from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import { abbreviateNumber, centsToDollars, getPercentage } from '@mm/utils';
import { ReactComponent as ExclamationIcon } from 'assets/red-info.svg';
import * as classnames from 'classnames';
import { capitalize, isEmpty, isNil, isNumber } from 'lodash';
import moment from 'moment';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import { compose } from 'recompose';
import { Dispatch } from 'redux';
import { PrimaryButton } from '../../../../../../../common/buttons/PrimaryButton/PrimaryButton';
import PrimarySelect, {
  DropdownOption,
} from '../../../../../../../common/components/PrimarySelect/PrimarySelect';
import { PrimaryToolTip } from '../../../../../../../common/components/PrimaryToolTip/PrimaryToolTip';
import {
  getUsedAndTotal,
  UsagePercentageBar,
} from '../../../../../../../common/components/UsagePercentageBar/UsagePercentageBar';
import { BillingPannelContext } from '../../../BillingPanel';
import OveragesManagement from '../OveragesManagement/OveragesManagement';
import { SeatsArea } from '../SeatsArea/SeatsArea';
import { SubscriptionMetaField } from '../SubscriptionMetaField/SubscriptionMetaField';
import { TeamOveragesArea } from '../TeamOveragesArea/TeamOveragesArea';
import { TeamPlanButtonsArea } from '../TeamPlanButtonsArea/TeamPlanButtonsArea';
import './ActivePlanDetails.pcss';

type OuterProps = {
  planTab: SubscriptionTabDefinition;
  searchInput: string;
  setSearchInput: (searchInput: string) => void;
  monthlyPlansAllotments: PlansAllotments;
  annualPlansAllotments: PlansAllotments;
  loadPricesForNextLevel: (subscriptionId: number, level: number) => void;
  openBillingModal: (props: Partial<OpenBillingModalParams>) => void;
};

type InnerProps = ReturnType<typeof mapDispatchToProps> &
  ReturnType<typeof mapStateToProps>;

type Props = OuterProps & InnerProps;

const getShowTeamOverageArea = (
  teamMessageStatistics?: TeamMessageStatistics,
  teamSubscription?: SubscriptionModel,
) => {
  if (teamMessageStatistics && teamSubscription) {
    const overMessageLimit =
      teamMessageStatistics.used >= teamMessageStatistics.messagesLimit;

    return overMessageLimit && !teamSubscription.overagesEnabled;
  }

  return false;
};

const getShowCloseToOverWarningIcon = (
  teamMessageStatistics?: TeamMessageStatistics,
  teamSubscription?: SubscriptionModel,
) => {
  if (teamSubscription && !teamSubscription.overagesEnabled) {
    return false;
  }

  if (teamMessageStatistics) {
    const usedPercent = getPercentage(
      teamMessageStatistics.used,
      teamMessageStatistics.messagesLimit,
    );

    return usedPercent > 79 && usedPercent < 100;
  }

  return false;
};

const renderTeamUpgradeButton = (
  teamTrial: boolean | undefined,
  isCompanyAdmin: boolean,
  defaultTeamPlan: EnhancedPlan | undefined,
  openBillingModal: (props: Partial<OpenBillingModalParams>) => void,
  landmineOpenId: number | undefined,
) => {
  if (!(teamTrial && isCompanyAdmin && defaultTeamPlan)) {
    return null;
  }
  return (
    <PrimaryButton
      className="billing-panel__upgrade-team-button"
      onClick={() =>
        openBillingModal({
          plan: defaultTeamPlan,
          landmineOpenId,
          stayOnPage: false,
        })
      }
    >
      Upgrade
    </PrimaryButton>
  );
};

// tslint:disable-next-line: cyclomatic-complexity
const ActivePlanDetails = (props: Props) => {
  const {
    planTab,
    accountDetails,
    isCompanyAdmin,
    defaultTeamPlan,
    openBillingModal,
    setSearchInput,
    searchInput,
    monthlyPlansAllotments,
    annualPlansAllotments,
    loadPricesForNextLevel,
    enhancedPlan,
  } = props;
  const {
    tabDetails: {
      billingPeriodPrice,
      sendAllotment,
      billedWith,
      nextBillingDate,
      teamMessageStatistics,
      extraPages,
    },
    shortName,
    teamSubscription,
    plan,
    pages,
    planCode,
    upgradePlansIds,
  } = planTab;

  const enhancedPlans = useSelector(enhancedPlansSelector);
  const connectedPages = useSelector(enhancedBillingPagesSelector);

  const facebookPageId = connectedPages.find(
    p => p.subscription?.id === teamSubscription?.id,
  )?.id;

  const facebookPage = connectedPages.find(p => p.id === facebookPageId);

  const currentSubscription = useSelector(
    getCurrentSubscription(facebookPageId),
  );

  const plansAllotments =
    currentSubscription?.billingPeriodLength === 'annually'
      ? annualPlansAllotments
      : monthlyPlansAllotments;

  const anyPageHasSmsActivated = useMemo(
    () =>
      connectedPages.filter(p => !isEmpty(p.subscription?.smsNumbers)).length >
      0,
    [connectedPages],
  );

  const pageBillingType = plan ? plan.pageBillingType : PageBillingType.Free;
  const dispatch = useDispatch();
  const planInfoMemoised = useMemo(() => {
    return getPlanInfoFromBillingType(pageBillingType);
  }, [pageBillingType]);

  const { landmineOpenId } = useContext(BillingPannelContext);

  const billingPeriod =
    (plan && plan.billingPeriod) || PlanBillingPeriodBE.month;
  const pageWithTrial = pages.find(p => p.redemption && p.redemption.teamTrial);
  const redemption = pageWithTrial && pageWithTrial.redemption;
  const teamTrial = planCode === TEAM_TRIAL_SUBSCRIPTION_PLAN_CODE;
  const showTeamOverageArea = getShowTeamOverageArea(
    teamMessageStatistics,
    teamSubscription,
  );
  const company = useSelector(activeCompanySelector);
  const changeSeats = () => {
    if (!teamSubscription && !currentSubscription) {
      return;
    }
    const seatsObject = {
      userSeats: (teamSubscription && currentSubscription?.userSeats) || 0,
      extraUserSeats:
        (teamSubscription && currentSubscription?.extraUserSeats) || 0,
    };
    if (company && plan) {
      openBillingModal({
        company,
        plan,
        seatsObject,
        facebookPage,
        subscription:
          (teamSubscription && currentSubscription) || teamSubscription,
      });
    }
  };

  const seatsMap = useSelector(getPlansUserSeats);
  // tslint:disable-next-line: cyclomatic-complexity
  const mapPlanSeatsAmount = () => {
    const { isProPlan, isProUnicorn, isAnyTeamPlan } = planInfoMemoised;
    if (!plan) {
      return 0;
    }

    return isProPlan
      ? seatsMap.pro
      : isProUnicorn
      ? seatsMap.unicorn
      : isAnyTeamPlan
      ? seatsMap.team
      : 0;
  };

  const showCloseToOverWarningIcon = getShowCloseToOverWarningIcon(
    teamMessageStatistics,
    teamSubscription,
  );
  const planDetailsName = classnames('active-plan-details', {
    'active-plan-details--trial': teamTrial,
  });
  const trialPeriod = redemption
    ? Math.max(-moment().diff(redemption.expiresAt, 'days'), 0)
    : 0;

  useEffect(() => {
    if (facebookPageId && !currentSubscription) {
      dispatch(
        subscriptionCRUDActions.getCurrentSubscription(facebookPageId, false),
      );
    }
  }, [currentSubscription, facebookPageId]);

  const mapPlanAllotmentOptions = (
    plansAllotments: {
      [key: string]: AllotmentDefinition;
    },
    getExtraAllotment: boolean,
  ) => {
    const {
      isProPlan,
      isProUnicorn,
      isAnyTeamPlan,
      isTeam2020,
      isCustomTeam,
      isLegacyTeamPlan,
      isSpecialTeam,
    } = planInfoMemoised;

    if (plan) {
      const planAllotment = isProPlan
        ? plansAllotments.pro
        : isProUnicorn
        ? plansAllotments.unicorn
        : isTeam2020
        ? plansAllotments.team2020
        : isCustomTeam || isLegacyTeamPlan
        ? plansAllotments.teamCustom
        : isAnyTeamPlan
        ? plansAllotments.team
        : null;
      if (!planAllotment) {
        return [];
      }

      const { start, msgPerLevel } = planAllotment;
      let planAllotmentLevel = planAllotment.levels + 1;
      if (!isNumber(planAllotment.levels) && isAnyTeamPlan) {
        planAllotmentLevel = plan.maxDisplayingSendAllotmentLevel;
      }

      return [...Array(planAllotmentLevel + 1).keys()].map(
        (level: number, index) => {
          const totalAllotmentNumber = start + msgPerLevel * level;
          const extraAllotmentNumber = msgPerLevel * level;
          return {
            id: index,
            name: getExtraAllotment
              ? `${extraAllotmentNumber}`
              : `${totalAllotmentNumber}`,
            level,
          };
        },
      );
    }
    return [];
  };

  const customOptionRenderer = (option: DropdownOption) => {
    let price = 0;
    const { level, name } = option;
    if (teamSubscription) {
      price = level * (teamSubscription.sendAllotmentAddOnPrice || 0);
    }

    const formattedName = String(name).replace(
      /(\d)(?=(\d{3})+(?!\d))/g,
      '$1,',
    );
    return (
      <span>
        {teamSubscription && (
          <span>
            <strong>${price}</strong>
          </span>
        )}
        {teamSubscription && <br />}
        <span className="active-plan-details__select">
          {formattedName} send credits{' '}
        </span>
      </span>
    );
  };

  const plansAllotmentsOptions = mapPlanAllotmentOptions(
    plansAllotments,
    false,
  );
  const extraPlansAllotmentsOptions = mapPlanAllotmentOptions(
    plansAllotments,
    true,
  );

  const [selectedLevel, setSelectedLevel] = useState(plansAllotmentsOptions[0]);
  const [selectedLevelChanged, setSelectedLevelChanged] = useState(false);

  const nextBillingAmount = currentSubscription?.expiresAt
    ? 'n/a'
    : `$${centsToDollars(currentSubscription?.nextInvoiceAmount?.sum || 0)}`;

  const nextBillPlanName = enhancedPlans.find(
    p => p.planCode === currentSubscription?.planCode,
  )?.shortName;

  const subscriptionPrice = currentSubscription?.nextInvoiceAmount?.subsPrice
    ? ` ($${centsToDollars(currentSubscription?.nextInvoiceAmount?.subsPrice)})`
    : '';

  const nextBillSubscription = nextBillPlanName
    ? `   ${nextBillPlanName} subscription${subscriptionPrice}`
    : '';

  const seatsPrice = currentSubscription?.nextInvoiceAmount?.addOns?.userSeats
    ?.priceTotal
    ? ` ($${centsToDollars(
        currentSubscription?.nextInvoiceAmount?.addOns.userSeats.priceTotal,
      )})`
    : '';

  const nextBillExtraSeats = seatsPrice
    ? ` + ${currentSubscription?.nextInvoiceAmount?.addOns?.userSeats?.quantity} Extra user seats${seatsPrice}`
    : '';

  const sendsPrice = currentSubscription?.nextInvoiceAmount?.addOns
    ?.sendAllotment?.priceTotal
    ? ` ($${centsToDollars(
        currentSubscription?.nextInvoiceAmount?.addOns.sendAllotment.priceTotal,
      )})`
    : '';

  const period =
    currentSubscription?.billingPeriodLength === 'annually' ? 'year' : 'month';

  const sendCreditsInfo =
    facebookPage?.messagesStatistic &&
    getUsedAndTotal(facebookPage.messagesStatistic, currentSubscription);

  const thisPeriodCredits = abbreviateNumber(
    plansAllotmentsOptions.find(
      o =>
        o.level ===
        currentSubscription?.currentInvoiceAmount?.addOns?.sendAllotment
          ?.quantity,
    )?.name ||
      sendCreditsInfo?.[1] ||
      '',
  );

  const nextPeriodExtraCredits = abbreviateNumber(
    extraPlansAllotmentsOptions.find(
      o =>
        o.level ===
        currentSubscription?.nextInvoiceAmount?.addOns?.sendAllotment?.quantity,
    )?.name || '',
  );

  const nextPeriodTotalCredits = abbreviateNumber(
    ['canceled', 'expired'].find(s => s === currentSubscription?.state)
      ? 1000
      : plansAllotmentsOptions.find(
          o =>
            o.level ===
            currentSubscription?.nextInvoiceAmount?.addOns?.sendAllotment
              ?.quantity,
        )?.name || '',
  );

  const nextBillExtraSends = sendsPrice
    ? ` + ${nextPeriodExtraCredits} extra sends${sendsPrice}`
    : '';

  const additionalPagesPrice = currentSubscription?.nextInvoiceAmount?.addOns
    ?.additionalPages?.priceTotal
    ? ` ($${centsToDollars(
        currentSubscription?.nextInvoiceAmount?.addOns.additionalPages
          .priceTotal,
      )})`
    : '';

  const nextBillAdditionalPages = additionalPagesPrice
    ? ` + ${currentSubscription?.nextInvoiceAmount?.addOns?.additionalPages?.quantity} Additional pages${additionalPagesPrice}`
    : '';

  const smsNumberPrice = currentSubscription?.nextInvoiceAmount?.addOns
    ?.smsNumber?.priceTotal
    ? ` ($${centsToDollars(
        currentSubscription?.nextInvoiceAmount?.addOns?.smsNumber.priceTotal,
      )})`
    : '';

  const nextBillSmsNumber = smsNumberPrice
    ? ` + ${currentSubscription?.nextInvoiceAmount?.addOns?.smsNumber?.quantity} SMS numbers${smsNumberPrice}`
    : '';

  const nextBillCombinedInfo =
    nextBillingAmount +
    nextBillSubscription +
    nextBillExtraSeats +
    nextBillAdditionalPages +
    nextBillSmsNumber +
    nextBillExtraSends;

  const allotedCredits = (
    <div className="active-plan-details__plan-info--period-details">
      {thisPeriodCredits && (
        <div className="active-plan-details__plan-info--period-details--left">
          This {period}: {thisPeriodCredits}
        </div>
      )}
      {nextPeriodTotalCredits && (
        <div className="active-plan-details__plan-info--period-details--right">
          Next {period}: {nextPeriodTotalCredits}
        </div>
      )}
    </div>
  );

  const thisPeriodSeats =
    currentSubscription?.currentInvoiceAmount?.addOns?.userSeats?.quantity;
  const nextPeriodSeats =
    currentSubscription?.nextInvoiceAmount?.addOns?.userSeats?.quantity;

  const additionalSeats = (
    <div className="active-plan-details__plan-info--period-details">
      {(thisPeriodSeats || thisPeriodSeats === 0) && (
        <div className="active-plan-details__plan-info--period-details--left">
          This {period}: {thisPeriodSeats}
        </div>
      )}
      {(nextPeriodSeats || nextPeriodSeats === 0) && (
        <div className="active-plan-details__plan-info--period-details--right">
          Next {period}: {nextPeriodSeats}
        </div>
      )}
    </div>
  );
  const isAllowedByLockStrategy =
    facebookPage?.lockStrategy === 'fb_page_level' ||
    (facebookPage?.lockStrategy === 'twilio_level' && anyPageHasSmsActivated);

  const showSendCreditsAllotmentsAndSeats = !(
    planInfoMemoised.isLegacyTeamPlan ||
    planInfoMemoised.isCustomTeam ||
    planInfoMemoised.isSpecialTeam ||
    planInfoMemoised.isLegacyProPlan
  );

  useEffect(() => {
    if (teamSubscription) {
      const { sendAllotmentLevel } = teamSubscription;

      const planAllotmentCurrent = plansAllotmentsOptions.find(allotment => {
        return Number(allotment.level) === sendAllotmentLevel;
      });
      if (sendAllotmentLevel && planAllotmentCurrent) {
        setSelectedLevel(planAllotmentCurrent);
      } else {
        setSelectedLevel(plansAllotmentsOptions[0]);
      }
    }
  }, [planInfoMemoised, JSON.stringify(plansAllotmentsOptions)]);

  const showDropDownForCustomTeam = () => {
    if (isCustomTeam) {
      if (isNil(teamSubscription?.maxSendAllotment)) {
        return true;
      }
      if (teamSubscription?.maxSendAllotment === 0) {
        return isEmpty(teamSubscription.smsNumbers);
      }
      return teamSubscription && teamSubscription?.maxSendAllotment > 0
        ? false
        : true;
    }
    return (
      isLegacyTeamPlan &&
      teamSubscription &&
      isEmpty(teamSubscription.smsNumbers)
    );
  };

  const LevelChoosesTemplate = () => {
    const { isAnyTeamPlan, isSpecialPlan } = planInfoMemoised;

    if (
      isSpecialPlan ||
      !plan ||
      !isAnyTeamPlan ||
      !plansAllotmentsOptions.length ||
      (teamSubscription && teamSubscription.expiresAt) ||
      showDropDownForCustomTeam()
    ) {
      return null;
    }

    const showPopup = isNumber(
      teamSubscription?.addOnDowngrades.allotmentLevelDowngrade,
    );
    const allotmentLevelDowngrade = teamSubscription?.addOnDowngrades
      .allotmentLevelDowngrade
      ? teamSubscription?.addOnDowngrades.allotmentLevelDowngrade
      : 0;
    const loweredSendsAmount = isNumber(allotmentLevelDowngrade)
      ? plansAllotmentsOptions.find(
          option => option.level === allotmentLevelDowngrade,
        )?.name
      : 0;

    return (
      <div>
        <div className="billing-page-card__extra-sends">
          Total{' '}
          {currentSubscription?.billingPeriodLength === 'annually'
            ? 'Annual'
            : 'Monthly'}{' '}
          Sends
        </div>
        <div className="active-plan-details__select-wrapper">
          <PrimarySelect
            showSearchFilter={false}
            className="active-plan-details__select"
            placeholder="Choose a plan"
            disabled={showPopup}
            onChange={(plan: { id: number; name: string; level: any }) =>
              onDropdownItemChange(plan)
            }
            options={plansAllotmentsOptions}
            value={selectedLevel}
            customOptionRenderer={customOptionRenderer}
          />
          {showPopup && (
            <PrimaryToolTip
              useHover={true}
              classNameContent="active-plan-details__tooltip-padding"
              anchorElement={<i className="far fa-question-circle" />}
            >
              Lowered since next billing cycle to {loweredSendsAmount || 0} Send
              credits
            </PrimaryToolTip>
          )}
          {selectedLevelChanged && (
            <PrimaryButton
              onClick={onOpenConfirmSendsModal}
              className="active-plan-details__action"
            >
              Change Limits
            </PrimaryButton>
          )}
        </div>
      </div>
    );
  };

  const onOpenConfirmSendsModal = () => {
    if (!teamSubscription) {
      return;
    }

    loadPricesForNextLevel(teamSubscription.id, selectedLevel.level);
    openBillingModal({
      level: selectedLevel,
      subscription: currentSubscription || teamSubscription,
      plan,
      facebookPage,
    });
    setSelectedLevelChanged(false);
  };

  const onDropdownItemChange = (plan: {
    id: number;
    name: string;
    level: number;
  }) => {
    setSelectedLevel(plan);
    setSelectedLevelChanged(true);
  };

  const {
    isAnyTeamPlan,
    isLegacyPlan,
    isCustomTeam,
    isCustomPro,
    isLegacyTeamPlan,
    isNewTeamPlan,
  } = planInfoMemoised;
  const currPlan = enhancedPlan.find(({ id }) => plan && plan.id === id);
  const isWp = currPlan
    ? currPlan.pageBillingType === PageBillingType.WP
    : false;
  const showPriceAndAllotment =
    isLegacyPlan || isCustomTeam || isCustomPro || isWp;

  const showOveragesArea =
    isCustomTeam || isLegacyTeamPlan
      ? teamSubscription &&
        !isEmpty(teamSubscription.smsNumbers) &&
        showTeamOverageArea
      : teamSubscription && showTeamOverageArea;

  const showUsagePercentageBar = isAnyTeamPlan && isAllowedByLockStrategy;

  const allowExtraAddOns =
    planInfoMemoised.isAnyTeamPlan &&
    isAllowedByLockStrategy &&
    teamSubscription &&
    currentSubscription;

  const subscriptionStatus = capitalize(teamSubscription?.state) || '';

  const subscriptionIsCanceled =
    teamSubscription?.state === 'canceled' ||
    teamSubscription?.downgradePending;

  const subscriptionEndsAt =
    subscriptionIsCanceled && teamSubscription
      ? moment(teamSubscription.endsAt).format('MM/DD/YYYY')
      : 'n/a';

  return (
    <div className={planDetailsName}>
      <div className="active-plan-details__header-top">
        <div className="active-plan-details__name-wrapper">
          <div className="active-plan-details__plan-name">{shortName}</div>

          {showPriceAndAllotment && billingPeriodPrice && (
            <div className="active-plan-details__price">
              ${billingPeriodPrice}/
              <span className="active-plan-details__price-billing-period">
                {billingPeriod}
              </span>
            </div>
          )}
        </div>

        {renderTeamUpgradeButton(
          teamTrial,
          isCompanyAdmin,
          defaultTeamPlan,
          openBillingModal,
          landmineOpenId,
        )}

        {teamSubscription && plan && (
          <TeamPlanButtonsArea
            upgradePlansIds={upgradePlansIds}
            showUpgradeButton={isLegacyTeamPlan || isNewTeamPlan}
            accountDetails={accountDetails}
            teamSubscription={teamSubscription}
            pages={pages}
            plan={plan}
            setSearchInput={setSearchInput}
            searchInput={searchInput}
            facebookPageId={facebookPageId}
          />
        )}
      </div>

      <div className="active-plan-details__body">
        <div className="active-plan-details__left-section">
          {planInfoMemoised.isLegacyPlan && (
            <div className="active-plan-details__legacy-plan-text">
              Learn more about your legacy plan
              <a
                className="active-plan-details__legacy-plan-link"
                target="_blank"
                rel="noopener noreferrer"
                href="https://mobilemonkey.com/help/help/article/d4f8r99li9-mobile-monkey-legacy-subscriptions"
              >
                here
              </a>
            </div>
          )}

          <div className="subscription-meta-container">
            {teamSubscription && extraPages !== undefined && (
              <SubscriptionMetaField
                label="ADDITIONAL PAGE SLOTS:"
                value={(extraPages as number).toString()}
              />
            )}
            {teamSubscription?.billingPeriodLength && (
              <SubscriptionMetaField
                label="Subscription Type:"
                value={capitalize(teamSubscription?.billingPeriodLength) || ''}
              />
            )}
            {teamSubscription && subscriptionStatus && (
              <SubscriptionMetaField
                label="Subscription Status:"
                value={subscriptionStatus}
              />
            )}
            {teamSubscription && subscriptionEndsAt && (
              <SubscriptionMetaField
                label="Plan expires:"
                value={subscriptionEndsAt}
              />
            )}
            {nextBillingDate && (
              <SubscriptionMetaField
                label="NEXT BILLING DATE:"
                value={nextBillingDate}
              />
            )}
            {showSendCreditsAllotmentsAndSeats && teamSubscription && (
              <SubscriptionMetaField
                label="Total Allotted Credits:"
                value={allotedCredits}
              />
            )}
            {showSendCreditsAllotmentsAndSeats &&
              teamSubscription &&
              !!(thisPeriodSeats || nextPeriodSeats) && (
                <SubscriptionMetaField
                  label="Additional Seats:"
                  value={additionalSeats}
                />
              )}
          </div>
        </div>

        <div className="active-plan-details__right-section">
          {planInfoMemoised.isAnyTeamPlan &&
            teamSubscription &&
            currentSubscription?.nextInvoiceAmount?.sum && (
              <SubscriptionMetaField
                label="NEXT BILL AMOUNT:"
                value={nextBillCombinedInfo}
              />
            )}

          {billedWith && (
            <SubscriptionMetaField label="BILLED WITH:" value={billedWith} />
          )}

          {teamTrial && (
            <SubscriptionMetaField
              label="Trial Period:"
              value={`${trialPeriod} days remaining`}
            />
          )}
        </div>
      </div>
      {teamMessageStatistics && showUsagePercentageBar && facebookPage && (
        <div className="active-plan-details__percentage-bar-wrapper">
          <UsagePercentageBar
            className="active-plan-details__percentage-bar"
            fbPage={facebookPage}
            isTeam={isAnyTeamPlan}
            isCustomTeam={isCustomTeam}
            anyPageHasSmsActivated={anyPageHasSmsActivated}
          />

          {showCloseToOverWarningIcon && (
            <ExclamationIcon className="active-plan-details__exclamation-icon" />
          )}
        </div>
      )}
      {teamSubscription && showOveragesArea && (
        <TeamOveragesArea
          subscription={teamSubscription}
          planInfo={planInfoMemoised}
        />
      )}
      {plan &&
        !!plan.outgoingMessagesLimit &&
        isAllowedByLockStrategy &&
        teamSubscription &&
        !showTeamOverageArea && (
          <OveragesManagement
            subscription={teamSubscription}
            className="active-plan-details__overages"
          >
            <a
              className="send-limit-alert-message__overage-management-link"
              href="https://mobilemonkey.com/help/article/03nc6a7axs-send-overage-automatic-upgrades"
              target="_blank"
              rel="noopener noreferrer"
            >
              View Pricing
            </a>
          </OveragesManagement>
        )}
      <div className="active-plan-details__row">
        {allowExtraAddOns && <LevelChoosesTemplate />}
        {teamSubscription &&
          currentSubscription &&
          teamSubscription?.userSeats &&
          !planInfoMemoised.isSpecialTeam && (
            <SeatsArea
              changeSeats={changeSeats}
              userSeatsInPlan={mapPlanSeatsAmount()}
              userSeats={currentSubscription?.userSeats}
              extraUserSeats={currentSubscription?.extraUserSeats}
              userSeatsDowngrade={
                currentSubscription?.addOnDowngrades?.userSeatsDowngrade
              }
              userSeatsUsed={currentSubscription?.userSeatsUsed}
              disabled={
                !!teamSubscription?.expiresAt &&
                teamSubscription?.state !== 'active'
              }
            />
          )}
      </div>
    </div>
  );
};

const mapStateToProps = (state: StoreState) => ({
  accountDetails: selectAccountDetails(state),
  defaultTeamPlan: defaultTeamPlanSelector(state),
  isCompanyAdmin: companyAdminSelector(state),
  facebookPage: activeFacebookPageSelector(state),
  enhancedPlan: enhancedPlansSelector(state),
});

const mapDispatchToProps = (dispatch: Dispatch<StoreState>) => ({
  openBillingModal: (props: OpenBillingModalParams) =>
    dispatch(modalActions.openBillingModal(props)),
});

const enhance = compose<InnerProps, OuterProps>(
  connect(mapStateToProps, mapDispatchToProps),
);

export default enhance(ActivePlanDetails);
